/**
 * PublicNoteDetail
 *
 * Read-only 1:1 reuse of the authenticated note detail body for public
 * shares. Composes the same building blocks (`DetailPanelLayout`,
 * `NoteDetailContentView`, `NoteDetailSidebarAside` with the minimap,
 * `NoteAttachmentList`) so the public note renders identically: author
 * (when the share exposes personal data), last-modified date, linked task,
 * minimap in fullscreen, and attachments streamed through the token-scoped
 * public note-attachment endpoint. History stays hidden on public.
 *
 * @module components/share/public/PublicArtifactDetail/parts/PublicNoteDetail
 */
import { memo, useCallback, useMemo } from 'react';
import { IconNote } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { CollapsibleTabItem } from '@/components/common/CollapsibleTabs';
import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import {
  DetailHeader,
  NoteAttachmentList,
  NoteDetailContentView,
  NoteDetailSidebarAside,
} from '@/components/notes/NoteDetailPanel';
import { useFilePreview } from '@/hooks/useFilePreview';
import {
  downloadAndSaveAttachment,
  noteAttachmentsToPreviewable,
} from '@/utils/attachmentDownload';
import type { Note, NoteAttachment } from '@/types/notes';

const CONTENT_TAB_ID = 'content';

interface PublicNoteDetailProps {
  /** Already-mapped note (mapped once in the parent, like the task path). */
  note: Note;
  token: string;
  /** Desktop fullscreen → sidebar layout with the minimap aside. */
  showSidebar: boolean;
  /** Omit to render the linked task as plain text (not covered by the share). */
  onTaskNavigate?: (taskId: string, title: string, shortId?: string) => void;
}

export const PublicNoteDetail = memo(function PublicNoteDetail({
  note,
  token,
  showSidebar,
  onTaskNavigate,
}: PublicNoteDetailProps) {
  const { t } = useTranslation();

  const previewableAttachments = useMemo(
    () => noteAttachmentsToPreviewable(note.attachments, token),
    [note.attachments, token]
  );
  const preview = useFilePreview(previewableAttachments);
  const { openPreview } = preview;

  const handleAttachmentDownload = useCallback(
    async (attachment: NoteAttachment) => {
      try {
        await downloadAndSaveAttachment({
          id: attachment.id,
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          source: 'publicNote',
          shareToken: token,
        });
      } catch (error) {
        console.error('Failed to download attachment:', error);
      }
    },
    [token]
  );

  const attachmentsContent = useMemo(() => {
    if (note.attachments.length === 0) return null;
    return (
      <NoteAttachmentList
        attachments={note.attachments}
        onPreview={openPreview}
        onDownload={handleAttachmentDownload}
      />
    );
  }, [note.attachments, openPreview, handleAttachmentDownload]);

  // History is never exposed publicly — the content section is the only one.
  const sectionTabs = useMemo<CollapsibleTabItem[]>(
    () => [
      {
        id: CONTENT_TAB_ID,
        label: t('notes.detail.tabs.content'),
        icon: IconNote,
      },
    ],
    [t]
  );

  const firstLinkedTask = note.linkedTasks[0];
  const hasLocation = note.latitude != null && note.longitude != null;
  const showMetadataSidebar = showSidebar && hasLocation;

  return (
    <>
      <DetailPanelLayout
        mode={showSidebar ? 'sidebar' : 'tabs'}
        sectionItems={sectionTabs}
        activeSection={CONTENT_TAB_ID}
        onSectionChange={() => undefined}
        sidebarHeader={<DetailHeader note={note} />}
        sidebarHeaderBordered={false}
        widePreview
        aside={
          showMetadataSidebar ? (
            <NoteDetailSidebarAside
              note={note}
              firstLinkedTask={firstLinkedTask}
              onTaskNavigate={onTaskNavigate}
              attachmentsContent={attachmentsContent}
            />
          ) : undefined
        }
      >
        <NoteDetailContentView
          note={note}
          activeTab={CONTENT_TAB_ID}
          showMetadataSidebar={showMetadataSidebar}
          firstLinkedTask={firstLinkedTask}
          onTaskNavigate={onTaskNavigate}
          attachmentsContent={attachmentsContent}
        />
      </DetailPanelLayout>

      <FilePreviewModal {...preview.modalProps} />
    </>
  );
});
